import React from 'react';
import { 
  Box, 
  Container,
  Typography, 
  Grid, 
  Button,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import WarningIcon from '@mui/icons-material/Warning';

// Images
import womanFanBackground from '../../assets/images/woman-with-fan-hot-humid.jpg';
import vocsIllustration from '../../assets/images/home-rdcv-humid-page-open-window-2.png';
import condensationBackground from '../../assets/images/condensation-gradient-rdcv-humid-page.jpg';
import humidClimateMap from '../../assets/images/humid-climate-zone-map-us.png';
import bundleImage from '../../assets/images/RMP MoCo Bundle (1).png';
import ventilatingDehuImage from '../../assets/images/ventilating-dehu.png';
import motorizedDamperImage from '../../assets/images/motorized-damper.png';
import aa1DiagramMonitor from '../../assets/images/aa1-diagram-monitor-1.png';
import aa1DiagramController from '../../assets/images/aa1-diagram-controller-1.png';
import bryanOrrImage from '../../assets/images/bryan-orr-rdcv-humid-page.png';
import ventilationIcon from '../../assets/images/Ventilation-icon-grey-on-white.png';
import humidityIcon from '../../assets/images/Humidity-icon-grey-on-white-1.png';
import filtrationIcon from '../../assets/images/Filtration-icon-grey-on-white.png';
import energyIcon from '../../assets/images/Energy-icon-grey-on-white.png';
import outdoorIcon from '../../assets/images/outdoor-icon-grey-on-white-1.png';
import smartIcon from '../../assets/images/smart-icon-grey-on-white-1.png';

// Styled components
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '80vh',
  display: 'flex',
  alignItems: 'center',
  background: `linear-gradient(to right, rgba(128, 128, 128, 0.8) 0%, rgba(128, 128, 128, 0.3) 70%, rgba(128, 128, 128, 0.1) 100%), url(${womanFanBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
}));





const ChallengesSection = styled(Box)(({ theme }) => ({
  minHeight: '70vh',
  background: `linear-gradient(135deg, rgba(0, 196, 193, 0.8) 0%, rgba(255, 229, 35, 0.8) 100%), url(${condensationBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
}));

const ResidentialVentilationPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h1" component="h1" sx={{ 
                fontWeight: 'bold', 
                mb: 3,
                fontSize: { xs: '2.5rem', md: '4rem' },
                lineHeight: 1.2,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
              }}>
            Residential Demand Control Ventilation
              </Typography>
              <Typography variant="h4" sx={{ 
                mb: 4,
                fontSize: { xs: '1.2rem', md: '1.8rem' },
                lineHeight: 1.4,
                maxWidth: '600px',
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}>
                Unlock the power of no-harm demand control ventilation, for healthier homes in humid climates.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Responsive Whole Home Ventilation Section */}
      <Box sx={{ backgroundColor: theme.palette.primary.main, py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ 
            textAlign: 'center',
            fontWeight: 'bold',
            color: 'white',
            mb: 2
          }}>
            RESPONSIVE WHOLE HOME VENTILATION MEETS HUMIDITY CONTROL
          </Typography>
        </Container>
      </Box>

      {/* Why Ventilation Matters Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            mb: 6,
            textAlign: 'center'
          }}>
            Why ventilation matters
          </Typography>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={vocsIllustration} 
                  alt="Home ventilation illustration" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '400px',
                    height: 'auto'
                  }} 
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" sx={{ 
                color: '#666',
                lineHeight: 1.8,
                fontSize: '1.2rem',
                mb: 4
              }}>
                A lack of ventilation allows airborne chemicals (VOCs) to build up inside a home, which can have a direct impact on human health. Ventilation is the most effective way of reducing chemical concentration — by diluting with fresh outdoor air.
          </Typography>
          <Button
                variant="outlined"
            size="large"
                href="https://haveniaq.com/2021/11/indoor-air-quality-introduction/"
                target="_blank"
                rel="noopener noreferrer"
            sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
              px: 4,
              py: 1.5,
              '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                  },
            }}
          >
                WHAT ARE VOCS?
          </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Challenges in Humid Climates Section */}
      <ChallengesSection sx={{ py: 8, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold',
            color: 'white',
            mb: 6,
            textAlign: 'center'
          }}>
            The challenges in humid climates
          </Typography>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <WarningIcon sx={{ fontSize: '2rem', color: 'white', mt: 0.5 }} />
                  <Typography variant="h5" sx={{ 
                    fontWeight: 'bold',
                    color: 'white',
                    lineHeight: 1.3
                  }}>
                    Without humidity control, ventilation can create conditions for mold
            </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <WarningIcon sx={{ fontSize: '2rem', color: 'white', mt: 0.5 }} />
                  <Typography variant="h5" sx={{ 
                    fontWeight: 'bold',
                    color: 'white',
                    lineHeight: 1.3
                  }}>
                    Improper ventilation adds heat load onto an HVAC system, increasing energy bills and shortening equipment life
            </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <WarningIcon sx={{ fontSize: '2rem', color: 'white', mt: 0.5 }} />
                  <Typography variant="h5" sx={{ 
                    fontWeight: 'bold',
                    color: 'white',
                    lineHeight: 1.3
                  }}>
                    Outdoor air conditions may be polluted, causing more harm than good
            </Typography>
                </Box>
              </Box>
          </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={humidClimateMap} 
                  alt="US humid climate zone map" 
                  style={{ 
                width: '100%',
                    maxWidth: '350px',
                    height: 'auto'
              }}
                />
            </Box>
          </Grid>
        </Grid>
      </Container>
      </ChallengesSection>

      {/* Equipment Visual Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold',
            color: '#455a64',
            mb: 8,
            textAlign: 'center'
          }}>
            The no-harm ventilation strategy
          </Typography>
          
          <Grid container spacing={6} alignItems="flex-start">
            {/* HAVEN Monitor & Controller Bundle */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  minHeight: '200px',
                  mb: 3
                }}>
                  <img 
                    src={bundleImage} 
                    alt="HAVEN Monitor and Controller Bundle" 
                    style={{ 
                      width: '100%', 
                      maxWidth: '220px',
                      height: 'auto'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: '#455a64',
                  mb: 2
                }}>
                  HAVEN's Central Air Monitor and Controller
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  The HAVEN Monitor measures particles, chemicals, and humidity levels in the home. The Controller activates equipment only when needed based on that data, for optimal efficiency.
                </Typography>
              </Box>
            </Grid>

            {/* Ventilating Dehumidifier */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  minHeight: '200px',
                  mb: 3
                }}>
                  <img 
                    src={ventilatingDehuImage} 
                    alt="Ventilating Dehumidifier" 
                    style={{ 
                      width: '100%', 
                      maxWidth: '220px',
                      height: 'auto'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: '#455a64',
                  mb: 2
                }}>
                  Ventilating Dehumidifier
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  With the capacity to ventilate, dehumidify, and filter out particles, a ventilating dehumidifier is able to address any Indoor Air Quality (IAQ) issues that HAVEN may detect.
                </Typography>
              </Box>
            </Grid>

            {/* Motorized Damper */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  minHeight: '200px',
                  mb: 3
                }}>
                  <img 
                    src={motorizedDamperImage} 
                    alt="Motorized Outdoor Damper" 
                    style={{ 
                      width: '100%', 
                      maxWidth: '160px',
                      height: 'auto'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: '#455a64',
                  mb: 2
                }}>
                  Motorized Outdoor Damper
                    </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#666',
                  lineHeight: 1.6,
                  fontSize: '0.95rem'
                }}>
                  HAVEN's integrated outdoor air quality intelligence enables control of motorized dampers, to shut off additional ventilation sources when conditions may be polluted.
                    </Typography>
              </Box>
              </Grid>
          </Grid>

          {/* Summary Text */}
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ 
              color: '#666',
              lineHeight: 1.6,
              fontSize: '1.1rem',
              fontStyle: 'italic',
              maxWidth: '800px',
              mx: 'auto'
            }}>
              This trio of equipment is the key for balancing residential ventilation needs with humidity control; adding data-driven automations to mechanical ventilation systems, without introducing harmful humidity or pollution problems.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Templates Section with Diagrams */}
      <Box sx={{ backgroundColor: theme.palette.primary.main, py: 8 }}>
        <Container maxWidth="lg">
          {/* Templates Title */}
          <Typography variant="h3" component="h2" sx={{ 
            fontWeight: 'bold',
            color: 'white',
            mb: 4,
            textAlign: 'center'
          }}>
            Residential Demand Control Ventilation Templates
          </Typography>

          {/* Description Text */}
          <Typography variant="body1" sx={{ 
            color: 'white',
            lineHeight: 1.6,
            fontSize: '1.2rem',
            textAlign: 'center',
            mb: 6,
            maxWidth: '800px',
            mx: 'auto'
          }}>
            Our tried and tested templates guide Technicians through each step, taking the guesswork out of getting started.
        </Typography>
        
          {/* Installation Diagrams */}
          <Grid container spacing={6} alignItems="center" sx={{ mb: 6 }}>
            {/* Monitor Diagram */}
          <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={aa1DiagramMonitor} 
                  alt="HAVEN Monitor Installation Diagram" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '300px',
                    height: 'auto'
                  }} 
                  />
              </Box>
          </Grid>

            {/* Controller Diagram */}
          <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={aa1DiagramController} 
                  alt="HAVEN Controller Installation Diagram" 
                  style={{ 
                width: '100%',
                    maxWidth: '300px',
                    height: 'auto'
              }}
                />
            </Box>
          </Grid>
        </Grid>

          {/* Action Buttons */}
          <Box sx={{ mb: 6, display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              href="https://support.pro.haveniaq.com/hc/en-us/articles/15449418856339-Residential-Demand-Control-Ventilation-1-Controller-Template-LAUNCHED-"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: 'white !important',
                color: '#1a365d !important',
                fontWeight: 'bold',
                px: 10,
                py: 3.5,
                fontSize: '1.4rem',
                textTransform: 'none',
                borderRadius: '12px',
                border: '3px solid white',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
                transform: 'scale(1.1)',
                zIndex: 10,
                position: 'relative',
                '&:hover': {
                  backgroundColor: '#f8f9fa !important',
                  boxShadow: '0 16px 50px rgba(0, 0, 0, 0.4)',
                  transform: 'scale(1.15) translateY(-2px)',
                },
              }}
            >
              I'M AN HVAC PRO - LET'S GO!
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="mailto:support@haveniaq.com?subject=Homeowner Inquiry - Residential Demand Control Ventilation&body=Hi, I'm a homeowner interested in finding a HAVEN Pro to implement demand control ventilation in my home. Please help me find a qualified professional in my area."
              sx={{
                borderColor: 'white',
                color: 'white',
                fontWeight: 'bold',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              I'M A HOMEOWNER, FIND ME A PRO
            </Button>
          </Box>


        </Container>
      </Box>

      {/* Bryan Orr Testimonial Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Bryan's Image */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <img 
                  src={bryanOrrImage} 
                  alt="Bryan Orr, HVAC School Podcast" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '350px',
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                  }} 
                />
              </Box>
            </Grid>
            
            {/* Quote Content */}
            <Grid item xs={12} md={8}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                {/* Large Quotation Mark */}
                <Typography variant="h1" sx={{
                  fontSize: '8rem',
                  fontWeight: 'bold',
                  color: theme.palette.primary.main,
                  lineHeight: 0.8,
                  fontFamily: 'Georgia, serif',
                  mb: 2,
                  display: 'block'
                }}>
                  "
                </Typography>
                
                {/* Quote Text */}
                <Typography variant="h3" sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  mb: 4,
                  fontSize: { xs: '1.6rem', md: '2.2rem' }
                }}>
                  This is a quantum leap forwards... maintaining a healthy atmosphere, but caring for the energy side as well.
                </Typography>
                
                {/* Attribution */}
                <Typography variant="h6" sx={{
                  color: '#666',
                  fontWeight: 'normal',
                  fontSize: '1.1rem'
                }}>
                  - <Box component="span" sx={{ fontWeight: 'bold' }}>Bryan Orr</Box>, HVAC School Podcast
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* All-in-one Automations Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #4ade80 0%, #22d3ee 100%)',
        py: 8 
      }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ 
            fontWeight: 'bold',
            color: 'white',
            mb: 6,
            textAlign: 'center'
          }}>
            All-in-one automations
          </Typography>
          
          <Grid container spacing={4}>
            {/* Top Row */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: '4px dashed rgba(255,255,255,0.3)'
                }}>
                  <img 
                    src={ventilationIcon} 
                    alt="Ventilation" 
                    style={{ 
                      width: '49px',
                      height: '49px'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  Ventilation
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: '4px dashed rgba(255,255,255,0.3)'
                }}>
                  <img 
                    src={humidityIcon} 
                    alt="Humidity control" 
                    style={{ 
                      width: '49px',
                      height: '49px'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  Humidity control
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: '4px dashed rgba(255,255,255,0.3)'
                }}>
                  <img 
                    src={filtrationIcon} 
                    alt="Filtration" 
                    style={{ 
                      width: '49px',
                      height: '49px'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  Filtration
                </Typography>
              </Box>
            </Grid>

            {/* Bottom Row */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: '4px dashed rgba(255,255,255,0.3)'
                }}>
                  <img 
                    src={energyIcon} 
                    alt="Energy efficiency" 
                    style={{ 
                      width: '49px',
                      height: '49px'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  Energy efficiency
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: '4px dashed rgba(255,255,255,0.3)'
                }}>
                  <img 
                    src={outdoorIcon} 
                    alt="Outdoor air aware" 
                    style={{ 
                      width: '49px',
                      height: '49px'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  Outdoor air aware
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{
                  width: 120,
                  height: 120,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                  border: '4px dashed rgba(255,255,255,0.3)'
                }}>
                  <img 
                    src={smartIcon} 
                    alt="Smart automations" 
                    style={{ 
                      width: '49px',
                      height: '49px'
                    }} 
                  />
                </Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold',
                  color: 'white'
                }}>
                  Smart automations
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>



      {/* Final CTA Section */}
      <Box sx={{ backgroundColor: '#535f66', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 3
                }}>
                  I'm an HVAC Pro
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'white',
                  mb: 4,
                  fontSize: '1.1rem'
                }}>
                  Interested in bringing residential demand control ventilation to my customers
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  href="https://pro.haveniaq.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  LEARN MORE
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 3
                }}>
                  I'm a homeowner
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: 'white',
                  mb: 4,
                  fontSize: '1.1rem'
                }}>
                  Interested in finding a HAVEN Pro to implement demand control ventilation in my home
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  href="https://haveniaq.com/homeowner-start"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 'bold',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  FIND A PRO
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ResidentialVentilationPage; 